<template>
  <b-collapse
    v-for="data in dataTypes"
    :key="data.id"
    :aria-id="$t('applications.dataset')"
    :aria-label="$t('applications.dataset')"
    :model-value="openAuthForScope"
    animation="slide"
    class="cardAuthWithScope"
  >
    <template #trigger="detail">
      <div
        :aria-expanded="!detail.open"
        :aria-controls="$t('applications.dataset')"
        class="card-header cardHeaderAuthWithScope"
      >
        <p
          class="card-header-title"
          :aria-label="data.name"
        >
          {{ data.name }}
        </p>
        <a
          class="card-header-icon"
          tabindex="0"
          @keyup.enter="openAuthForScope = !openAuthForScope"
          @keyup.space="openAuthForScope = !openAuthForScope"
        >
          <b-icon
            :icon="detail.open ? 'chevron-up' : 'chevron-down'"
            type="is-primary"
          />
        </a>
      </div>
    </template>
    <div class="card-content">
      <div class="content">
        <AuthorizationTableForOneDataType
          :application="application"
          :application-name="application.name"
          :authorization="getAuthorization(data.id)"
          :data="data"
          :data-name="data.id"
          :has-dependencies="hasDependencies"
          :list-column-scope="columnsName"
          :reference-scope="referencesScopes[data.id]"
          :type-time-scope="typeTimeScope(data.id)"
          :with-time-scope="withTimeScope(data.id)"
          :is-disable="isDisable"
          @update:propagate-changes="modifyAuthorizations($event)"
          @update:operation-type="modifyOperationTypes(data.id, $event)"
          @update:date-value-interval="modifyTimescope(data.id, $event)"
        />
      </div>
    </div>
  </b-collapse>
</template>

<script lang="ts">
import { computed, PropType } from "vue";
import AuthorizationTableForOneDataType from "@/components/common/authorization/AuthorizationTableForOneDataType.vue";
import useBoolean from "@/composable/components/boolean";
import "@/style/components/AuthorizationForScope.scss";
import { BCollapse } from "buefy";
import { ListColumnScope } from "@/types/front/ListColumnName";

export default {
  name: "AuthorizationForScope",
  components: { BCollapse, AuthorizationTableForOneDataType },
  props: {
    authorization: {
      type: Object,
      required: true,
    },
    authorizationsDescription: {
      type: Object,
      required: true,
    },
    application: {
      type: Object,
      required: true,
    },
    dataTypes: {
      type: Object,
      required: true,
    },
    listColumnScope: {
      type: Object as PropType<ListColumnScope>,
      required: true,
    },
    referencesScopes: {
      type: Object,
      required: true,
    },
    hasDependencies: {
      type: Array,
      default: () => [],
    },
    isDisable: {
      type: Boolean,
      default: false,
      required: false,
    },
  },
  emits: ["update:authorization"],
  setup(props, { emit }) {
    const { refBoolean: openAuthForScope } = useBoolean(false);
    const columnsName = computed(() => props.listColumnScope || {});

    const withTimeScope = computed(
      () => (dataId: string) =>
        Object.keys(props.authorizationsDescription?.[dataId] || {}).includes("timeScope"),
    );

    const typeTimeScope = computed(() => (dataId: string) => {
      if (props.authorizationsDescription?.[dataId].timeScope) {
        return props.application.configuration.dataDescription[dataId].componentDescriptions[
          props.authorizationsDescription[dataId].timeScope
        ].checker.type === "DateChecker"
          ? "date"
          : "";
      }
      else {
        return withTimeScope.value(dataId) ? props.authorizationsDescription?.[dataId]?.timeScope : "";
      }
    });

    const createDefaultAuthorization = () => ({
      operationTypes: [],
      requiredAuthorizations: {},
      fromDay: [-999999999, 1, 1],
      toDay: [999999999, 12, 31],
      hasExtraction: false,
    });

    const getAuthorization = dataId =>
      props.authorization[dataId] || createDefaultAuthorization();

    const updateAuthorization = (datatype, updater) => {
      const authorization = { ...props.authorization };
      authorization[datatype] = authorization[datatype] || createDefaultAuthorization();
      updater(authorization[datatype]);
      emit("update:authorization", authorization);
    };

    const modifyTimescope = (datatype, event) => {
      updateAuthorization(datatype, (auth) => {
        auth.fromDay = event.interval.from;
        auth.toDay = event.interval.to;
        auth.format = event.interval.format;
      });
    };

    const modifyOperationTypes = (datatype, operationTypes) => {
      updateAuthorization(datatype, (auth) => {
        auth.operationTypes = operationTypes;
      });
    };

    const modifyAuthorizations = ({ changes, datatype, scope }) => {
      updateAuthorization(datatype, (auth) => {
        auth.requiredAuthorizations = auth.requiredAuthorizations || {};
        auth.requiredAuthorizations[scope] = auth.requiredAuthorizations[scope] || [];

        changes.forEach((change) => {
          const index = auth.requiredAuthorizations[scope].indexOf(change.path);
          if (change.remove) {
            if (index !== -1) {
              auth.requiredAuthorizations[scope].splice(index, 1);
            }
          }
          else if (index === -1) {
            auth.requiredAuthorizations[scope].push(change.path);
          }
        });
      });
    };

    return {
      columnsName,
      withTimeScope,
      typeTimeScope,
      getAuthorization,
      modifyTimescope,
      modifyOperationTypes,
      modifyAuthorizations,
      openAuthForScope,
    };
  },
};
</script>
